import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { FormSpy } from 'react-final-form';
import { flatten } from 'lodash';

import {
  Accordion,
  Row,
  Col,
  Loading,
} from '@folio/stripes/components';

import {
  SelectUserLookUp,
  RemoveUserAssignment,
} from '../../../../common';
import {
  useCallout,
} from '../../../../../hooks';
import {
  TASK_FIELDS,
  TASK_ASSIGN_TO_FIELDS,
  LOAD_LIMIT,
  TEAM_FIELDS,
  LOAD_LIMIT_MAX,
} from '../../../../../constants';

import {
  RenderSelectAssignmentSection,
} from './components';

const TaskAssignment = ({
  form,
  stripes,
  mutator,
}) => {
  const { change } = form;
  const {
    team,
    metadata,
    assignTo,
  } = form.getState().initialValues;

  const showCallout = useCallout();

  const [teams, setTeams] = useState([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [users, setUsers] = useState(null);

  const [editTaskAssignmentLoaded, setEditTaskAssignmentLoaded] = useState(false);
  const loadAssignment = metadata && assignTo === TASK_ASSIGN_TO_FIELDS.TEAM;

  const isTeamUsersLoaded = (teamUsers) => Array.isArray(teamUsers);

  const getTeams = useCallback(() => {
    setTeams([]);
    setTeamsLoaded(false);
    mutator.teams.GET({
      params: {
        limit: LOAD_LIMIT_MAX,
      },
    })
      .then((response) => {
        setTeams(response.teams);
        setTeamsLoaded(true);
      })
      .catch(() => {
        showCallout({
          type: 'error',
          message: <FormattedMessage id="ui-task-list.validation.communicationProblem" />,
        });
      });
  }, []);

  const getUsers = (value) => {
    setUsers(null);

    const selectedTeamObj = teams.find((selectedTeam) => selectedTeam.id === value);
    const userIds = [...selectedTeamObj.members] || [];
    const usersPromises = [];

    while (userIds.length) {
      usersPromises.push(mutator.users.GET({
        params: {
          limit: LOAD_LIMIT,
          query: userIds.splice(0, LOAD_LIMIT)
            .map(userId => `id==${userId}`)
            .join(' or '),
        }
      }));
    }

    return Promise.all(usersPromises)
      .then((userArrays) => {
        const combinedUsers = flatten(userArrays.map((arr) => arr.users));

        setUsers(combinedUsers);

        if (loadAssignment) {
          setEditTaskAssignmentLoaded(true);
        }
      })
      .catch(() => {
        showCallout({
          type: 'error',
          message: <FormattedMessage id="ui-task-list.validation.communicationProblem" />,
        });
      });
  };

  const removeUserAssignment = () => {
    change(TASK_FIELDS.ASSIGN_TO, '');
    change(`${TASK_FIELDS.TEAM}.${TEAM_FIELDS.ID}`, '');
    change(`${TASK_FIELDS.TEAM}.${TEAM_FIELDS.NAME}`, '');
    change(TASK_FIELDS.ASSIGNED_USER_ID, '');
    change(TASK_FIELDS.ASSIGNED_USER_NAME, '');
  };

  const unAssignUserButton = () => (
    <RemoveUserAssignment onClick={removeUserAssignment} />
  );

  useEffect(
    () => {
      getTeams();
    },
    [],
  );

  useEffect(
    () => {
      const teamId = team[TEAM_FIELDS.ID];

      if (teamId && teams.length) {
        getUsers(teamId);
      }
    },
    [teams],
  );

  return (
    <FormSpy subscription={{ values: true }}>
      {({ values }) => {
        const displayUnassignBtn = values[TASK_FIELDS.ASSIGN_TO] && teams.length ? unAssignUserButton() : null;
        const isLoading = (loadAssignment && !editTaskAssignmentLoaded)
          || (!values[TASK_FIELDS.ASSIGN_TO] && !teamsLoaded);

        return (
          <Accordion
            id="taskAssignment"
            label={<FormattedMessage id="ui-task-list.title.accordion.taskAssignment" />}
          >
            {
              isLoading ? (
                <Row middle="sm">
                  <Col sm={6}>
                    <Loading />
                  </Col>
                </Row>
              ) : (
                <Row>
                  {
                    teamsLoaded && !teams.length ? (
                      <SelectUserLookUp
                        change={change}
                        noTeams={teamsLoaded && !teams.length}
                        stripes={stripes}
                      />
                    ) : (
                      <>
                        <Col sm={9}>
                          <Row>
                            <RenderSelectAssignmentSection
                              isUsersLoaded={isTeamUsersLoaded(users)}
                              stripes={stripes}
                              teams={teams}
                              users={users}
                              values={values}
                              change={change}
                              getTeams={getTeams}
                              getUsers={getUsers}
                            />
                          </Row>
                        </Col>
                        <Col sm={3}>
                          {
                            displayUnassignBtn
                          }
                        </Col>
                      </>
                    )
                  }
                </Row>
              )
            }

          </Accordion>
        );
      }}
    </FormSpy>
  );
};

TaskAssignment.propTypes = {
  form: PropTypes.shape({
    change: PropTypes.func.isRequired,
    getState: PropTypes.func.isRequired,
  }).isRequired,
  stripes: PropTypes.shape({
    user: PropTypes.shape({
      user: PropTypes.object.isRequired,
    }).isRequired,
  }).isRequired,
  mutator: PropTypes.shape({
    teams: PropTypes.shape({
      GET: PropTypes.func.isRequired
    }),
    users: PropTypes.shape({
      GET: PropTypes.func.isRequired
    }),
  })
};

export default TaskAssignment;
